import {formatMoney} from '@shopify/theme-currency';
import * as Sqrl from 'squirrelly';

import * as a11y from '../vendor/theme-scripts/theme-a11y';
import slideToggle from '../util/slide-toggle';
import slideUp from '../util/slide-up';
import slideDown from '../util/slide-down';
import getSiblings from '../util/siblings';
import debounce from '../util/debounce';
import FetchError from '../util/fetch-error';

import QuantityCounter from './quantity-handle';

const settings = {
  cartDrawerEnabled: window.theme.cartDrawerEnabled,
  times: {
    timeoutAddProduct: 1000,
    closeDropdownAfter: 5000,
  },
  classes: {
    hidden: 'is-hidden',
    added: 'is-added',
    htmlClasses: 'has-open-cart-dropdown',
    open: 'is-open',
    active: 'is-active',
    visible: 'is-visible',
    loading: 'is-loading',
    disabled: 'is-disabled',
    success: 'is-success',
    error: 'has-error',
    headerStuck: 'js__header__stuck',
    drawerVisible: 'drawer--visible',
  },
  attributes: {
    transparent: 'data-header-transparent',
    upsellButton: 'data-upsell-btn',
  },
  elements: {
    html: 'html',
    cartDropdown: '#cart-dropdown',
    cartDropdownBody: '[data-cart-dropdown-body]',
    emptyMessage: '[data-empty-message]',
    buttonHolder: '[data-foot-holder]',
    itemsHolder: '[data-items-holder]',
    item: '[data-item]',
    cartToggleElement: '[data-cart-toggle]',
    cartItemRemove: '[data-item-remove]',
    cartCount: '[data-cart-count]',
    cartCountValue: 'data-cart-count',
    clickedElementForExpanding: '[data-expand-button]',
    cartWidget: '[data-cart-widget]',
    cartTotal: '[data-cart-total]',
    cartMessage: '[data-cart-message]',
    cartMessageValue: 'data-cart-message',
    buttonAddToCart: '[data-add-to-cart]',
    formErrorsContainer: '[data-cart-errors-container]',
    cartErrors: '[data-cart-errors]',
    cartCloseError: '[data-cart-error-close]',
    formCloseError: '[data-close-error]',
    quickAddHolder: '[data-quick-add-holder]',
    cartProgress: '[data-cart-progress]',
    cartOriginalTotal: '[data-cart-original-total]',
    cartOriginaTotalPrice: '[data-cart-original-total-price]',
    cartDiscountsHolder: '[data-cart-discounts-holder]',
    headerWrapper: '[data-header-wrapper]',
    burgerButton: '[data-drawer-toggle]',
    upsellHolder: '[data-upsell-holder]',
    errorMessage: '[data-error-message]',
    navDrawer: '[data-drawer]',
  },
  formatMoney: theme.moneyFormat,
  cartTotalDiscountsTemplate: '[data-cart-total-discount]',
};

class CartDrawer {
  constructor() {
    if (window.location.pathname === '/password') {
      return;
    }

    this.init();
  }

  init() {
    this.settings = settings;

    // DOM Elements
    this.document = document;
    this.html = this.document.querySelector(this.settings.elements.html);
    this.cartDropdown = this.document.querySelector(this.settings.elements.cartDropdown);
    this.cartDropdownBody = this.document.querySelector(this.settings.elements.cartDropdownBody);
    this.emptyMessage = this.document.querySelector(this.settings.elements.emptyMessage);
    this.buttonHolder = this.document.querySelector(this.settings.elements.buttonHolder);
    this.itemsHolder = this.document.querySelector(this.settings.elements.itemsHolder);
    this.items = this.document.querySelectorAll(this.settings.elements.item);
    this.counterHolders = this.document.querySelectorAll(this.settings.elements.cartCount);
    this.cartTotal = this.document.querySelector(this.settings.elements.cartTotal);
    this.cartMessage = this.document.querySelector(this.settings.elements.cartMessage);
    this.cartOriginalTotal = this.document.querySelector(this.settings.elements.cartOriginalTotal);
    this.cartOriginaTotalPrice = this.document.querySelector(this.settings.elements.cartOriginaTotalPrice);
    this.cartDiscountHolder = this.document.querySelector(this.settings.elements.cartDiscountsHolder);
    this.clickedElementForExpanding = this.document.querySelectorAll(this.settings.elements.clickedElementForExpanding);
    this.cartTotalDiscountTemplate = this.document.querySelector(this.settings.cartTotalDiscountsTemplate).innerHTML;
    this.cartErrorHolder = this.document.querySelector(this.settings.elements.cartErrors);
    this.cartCloseErrorMessage = this.document.querySelector(this.settings.elements.cartCloseError);
    this.headerWrapper = this.document.querySelector(this.settings.elements.headerWrapper);
    this.headerIsTransparent = this.headerWrapper.getAttribute(this.settings.attributes.transparent) !== 'false';
    this.accessibility = a11y;
    this.navDrawer = this.document.querySelector(this.settings.elements.navDrawer);

    this.form = null;

    this.build = this.build.bind(this);

    // AJAX request
    this.addToCart = this.addToCart.bind(this);
    this.updateCart = this.updateCart.bind(this);

    // Cart events
    this.openCartDropdown = this.openCartDropdown.bind(this);
    this.closeCartDropdown = this.closeCartDropdown.bind(this);
    this.toggleCartDropdown = this.toggleCartDropdown.bind(this);

    // Checking
    this.hasItemsInCart = this.hasItemsInCart.bind(this);

    // Set classes
    this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

    // Flags
    this.cartDropdownIsBuilded = this.items.length > 0;
    this.totalItems = this.cartDropdownIsBuilded;
    this.cartDropdownIsOpen = false;
    this.cartDiscounts = 0;
    this.cartDrawerEnabled = this.settings.cartDrawerEnabled;
    this.cartLimitErrorIsHidden = true;

    this.html.style.setProperty('--scrollbar-width', `${window.innerWidth - this.html.clientWidth}px`);

    // Cart Events
    this.eventToggleCart();
    this.expandEvents();
    this.cartEvents();
    this.cartEventAdd();

    // Init quantity for fields
    this.initQuantity();

    this.customEventAddProduct();

    // Init estimate shipping calculator
    this.estimateShippingCalculator();

    // Attributes
    if (this.cartMessage) {
      this.cartFreeLimitShipping = Number(this.cartMessage.getAttribute('data-limit')) * 100;
      this.subtotal = 0;
      this.progress = this.document.querySelector(this.settings.elements.cartProgress);
      this.circumference = 28 * Math.PI; // radius - stroke * 4 * PI

      this.setProgress(this.progress.getAttribute('data-percent'));
    }
  }

  /**
   * Init quantity field functionality
   *
   * @return  {Void}
   */

  initQuantity() {
    this.items = this.document.querySelectorAll(this.settings.elements.item);

    this.items.forEach((item) => {
      const initQuantity = new QuantityCounter(item, true);

      initQuantity.init();
      this.customEventsHandle(item);
    });
  }

  /**
   * Expand blocks and close siblings
   *
   * @return  {Void}
   */

  expandEvents() {
    const that = this;

    this.clickedElementForExpanding.forEach((item) => {
      item.addEventListener('click', function (event) {
        event.preventDefault();

        item.classList.toggle(that.settings.classes.active);
        slideToggle(that.document.querySelector(item.getAttribute('href')), 400);

        const otherCartWidget = getSiblings(item.closest(that.settings.elements.cartWidget)).filter((el) => el.hasAttribute(that.settings.elements.cartWidget.replace('[', '').replace(']', '')))[0];

        if (otherCartWidget) {
          const otherClickedElementForExpanding = otherCartWidget.querySelector(that.settings.elements.clickedElementForExpanding);

          otherClickedElementForExpanding.classList.remove(that.settings.classes.active);
          slideUp(otherClickedElementForExpanding.nextElementSibling, 400);
        }
      });
    });
  }

  /**
   * Custom event who change the cart
   *
   * @return  {Void}
   */

  customEventsHandle(holder) {
    holder.addEventListener(
      'update-cart',
      debounce((event) => {
        this.updateCart(
          {
            id: event.detail.id,
            quantity: event.detail.quantity,
          },
          holder,
          event.detail.valueIsEmpty
        );
      }, 500)
    );
  }

  /**
   *  Custom event for add product to the cart
   */
  customEventAddProduct() {
    this.html.addEventListener(
      'cart:add-to-cart',
      debounce((event) => {
        this.addToCart(JSON.stringify(event.detail.data), event.detail);
      }, 500)
    );
  }

  /**
   * Cart events
   *
   * @return  {Void}
   */

  cartEvents() {
    const that = this;
    const cartItemRemove = this.document.querySelectorAll(that.settings.elements.cartItemRemove);

    cartItemRemove.forEach((item) => {
      item.addEventListener('click', function (event) {
        event.preventDefault();

        that.updateCart({
          id: this.getAttribute('data-id'),
          quantity: 0,
        });
      });
    });

    if (this.cartCloseErrorMessage) {
      this.cartCloseErrorMessage.addEventListener('click', (event) => {
        event.preventDefault();

        slideUp(this.cartErrorHolder, 400);
      });
    }
  }

  /**
   * Cart event add product to cart
   *
   * @return  {Void}
   */

  cartEventAdd() {
    this.document.addEventListener('click', (event) => {
      const clickedElement = event.target;

      if (clickedElement.matches(this.settings.elements.buttonAddToCart) || (clickedElement.closest(this.settings.elements.buttonAddToCart) && clickedElement)) {
        event.preventDefault();

        let button = clickedElement.matches(this.settings.elements.buttonAddToCart) ? clickedElement : clickedElement.closest(this.settings.elements.buttonAddToCart);

        if (button.hasAttribute(this.settings.attributes.upsellButton)) {
          button.classList.add(this.settings.classes.loading);
        } else {
          button = null;
        }

        if (clickedElement.hasAttribute('disabled') || clickedElement.parentNode.hasAttribute('disabled')) {
          return;
        }

        this.form = clickedElement.closest('form');
        const formData = new FormData(this.form);

        if (this.form.querySelector('[type="file"]')) {
          return;
        }

        const formString = new URLSearchParams(formData).toString();

        this.addToCart(formString, null, button);

        this.html.dispatchEvent(
          new CustomEvent('cart:add-item', {
            bubbles: true,
            detail: {
              selector: clickedElement,
            },
          })
        );
      }
    });
  }

  /**
   * Estimate shippint calculator
   *
   * @return  {Void}
   */

  estimateShippingCalculator() {
    Shopify.Cart.ShippingCalculator.show({
      submitButton: theme.strings.shippingCalcSubmitButton,
      submitButtonDisabled: theme.strings.shippingCalcSubmitButtonDisabled,
      customerIsLoggedIn: theme.customerLoggedIn,
      moneyFormat: theme.moneyWithCurrencyFormat,
    });
  }

  /**
   * Get response from the cart
   *
   * @return  {Void}
   */

  getCart() {
    fetch('/cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.updateCounter(response.item_count);
        this.newTotalItems = response.items.length;

        this.buildTotalPrice(response);
        this.freeShippingMessageHandle(response.total_price);

        if (this.cartMessage) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        return fetch('/cart?view=items');
      })
      .then((response) => response.text())
      .then((response) => {
        this.build(response);
      })
      .catch((error) => console.log(error));
  }

  /**
   * Add item(s) to the cart and show the added item(s)
   *
   * @param   {String}  data
   * @param   {DOM Element/Object}  quickAddHolder
   * @param   {DOM Element}  button
   *
   * @return  {Void}
   */

  addToCart(data, quickAddHolder = null, button = null) {
    // Get Quick Add form
    if (this.form === null && quickAddHolder !== null && quickAddHolder.label) {
      this.form = quickAddHolder.label.parentNode.querySelector('form');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data,
    })
      .then((response) => response.json())
      .then((response) => {
        if (button) {
          button.setAttribute('disabled', 'disabled');
        }

        if (response.status) {
          if (quickAddHolder !== null) {
            this.addToCartError(response, quickAddHolder.element, button);
          } else {
            this.addToCartError(response, null, button);
          }

          return;
        }

        if (this.cartDrawerEnabled) {
          if (quickAddHolder !== null && quickAddHolder.label) {
            quickAddHolder.label.classList.remove(this.settings.classes.hidden, this.settings.classes.loading);
            quickAddHolder.label.classList.add(this.settings.classes.added);
          }

          this.getCart();

          setTimeout(() => {
            if (button !== null) {
              button.classList.remove(this.settings.classes.loading);
              button.removeAttribute('disabled');
              button.classList.add(this.settings.classes.success);
            }

            this.openCartDropdown();
            this.cartDropdownIsOpen = true;
          }, this.settings.times.timeoutAddProduct);
        } else {
          window.location = theme.routes.cart;
        }
      })
      .catch((error) => console.log(error));
  }

  /**
   * Update cart
   *
   * @param   {Object}  updateData
   *
   * @return  {Void}
   */

  updateCart(updateData = {}, holder = null, valueIsEmpty = false) {
    let newCount = null;
    let oldCount = null;
    let newItem = null;
    let settedQuantity = updateData.quantity;

    if (holder !== null) {
      holder.closest(this.settings.elements.item).classList.add(this.settings.classes.loading);
    }

    this.items.forEach((item) => {
      item.classList.add(this.settings.classes.disabled);
      item.querySelector('input').blur();
    });

    fetch('/cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        const matchKeys = (item) => item.key === updateData.id;
        const index = response.items.findIndex(matchKeys);
        oldCount = response.item_count;
        newItem = response.items[index].title;

        const data = {
          line: `${index + 1}`,
          quantity: settedQuantity,
        };

        return fetch('/cart/change.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data),
        });
      })
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        newCount = response.item_count;

        if (valueIsEmpty) {
          settedQuantity = 1;
        }

        if (settedQuantity !== 0) {
          this.cartLimitErrorIsHidden = newCount !== oldCount;

          this.toggleLimitError(newItem);
        }

        this.updateCounter(newCount);

        // Change the cart total and hide message if missing discounts and the changed product is not deleted
        this.buildTotalPrice(response);
        this.freeShippingMessageHandle(response.total_price);
        this.cartDiscounts = response.total_discount;

        // Build cart again if the quantity of the changed product is 0 or cart discounts are changed
        if (this.cartMessage) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        this.getCart();
      })
      .catch((error) => console.log(error));
  }

  /**
   * Show/hide limit error
   *
   * @param   {String}  itemTitle
   *
   * @return  {Void}
   */

  toggleLimitError(itemTitle) {
    this.cartErrorHolder.querySelector(this.settings.elements.errorMessage).innerText = itemTitle;

    if (this.cartLimitErrorIsHidden) {
      slideUp(this.cartErrorHolder, 400);
    } else {
      slideDown(this.cartErrorHolder, 400);
    }
  }

  /**
   * Handle errors
   *
   * @param   {Object}  response
   *
   * @return  {Object}
   */

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  /**
   * Add to cart error handle
   *
   * @param   {Object}  data
   * @param   {DOM Element/Null} quickAddHolder
   * @param   {DOM Element/Null} button
   *
   * @return  {Void}
   */

  addToCartError(data, quickAddHolder, button) {
    if (this.cartDrawerEnabled) {
      this.closeCartDropdown();
    }

    let errorContainer = this.document.querySelector(this.settings.elements.formErrorsContainer);

    if (button !== null) {
      errorContainer = button.closest(this.settings.elements.upsellHolder).querySelector(this.settings.elements.formErrorsContainer);
      button.classList.remove(this.settings.classes.loading);
      button.removeAttribute('disabled');
    }

    if (errorContainer) {
      errorContainer.innerHTML = `<div class="errors">${data.message}: ${data.description}<span class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close-thin" viewBox="0 0 27 27"><g stroke="#979797" fill="none" fill-rule="evenodd" stroke-linecap="square"><path d="M.5.5l26 26M26.5.5l-26 26"></path></g></svg></span></div>`;

      errorContainer.classList.add(this.settings.classes.visible);

      document.dispatchEvent(new CustomEvent('product:bar:error', {bubbles: false}));
    }

    if (quickAddHolder) {
      this.html.dispatchEvent(
        new CustomEvent('cart:add-to-error', {
          bubbles: true,
          detail: {
            message: data.message,
            description: data.description,
            holder: quickAddHolder,
          },
        })
      );
    }

    this.document.addEventListener('click', (event) => {
      const clickedElement = event.target;

      if (clickedElement.matches(this.settings.elements.formCloseError) || clickedElement.closest(this.settings.elements.formCloseError)) {
        event.preventDefault();

        errorContainer.classList.remove(this.settings.classes.visible);
      }
    });
  }

  /**
   * Open cart dropdown and add class on body
   *
   * @return  {Void}
   */

  openCartDropdown() {
    document.dispatchEvent(
      new CustomEvent('theme:drawer:close', {
        bubbles: false,
      })
    );

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDropdown}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDropdownBody}));

    this.html.classList.add(this.settings.classes.htmlClasses);
    this.cartDropdown.classList.add(this.settings.classes.open);

    this.accessibility.removeTrapFocus();
    this.accessibility.trapFocus(this.cartDropdown, {
      elementToFocus: this.cartDropdown.querySelector('a:first-child, input:first-child'),
    });

    this.headerIsTransparent = this.headerWrapper.getAttribute(this.settings.attributes.transparent) !== 'false';
    this.headerWrapper.setAttribute(this.settings.attributes.transparent, false);

    if (!this.cartDropdownIsBuilded) {
      this.getCart();
    }
  }

  /**
   * Close cart dropdown and remove class on body
   *
   * @return  {Void}
   */

  closeCartDropdown() {
    this.document.dispatchEvent(
      new CustomEvent('theme:cart-close', {
        bubbles: true,
      })
    );

    this.accessibility.removeTrapFocus();

    slideUp(this.cartErrorHolder, 400);

    if (this.html.classList.contains('is-focused')) {
      const button = this.document.querySelector(`${this.settings.elements.cartToggleElement}[data-focus-element]`);

      setTimeout(() => {
        button.focus();
      }, 200);
    }

    const upsellButton = this.document.querySelector(`[${this.settings.attributes.upsellButton}].${this.settings.classes.success}`);

    if (upsellButton) {
      setTimeout(() => {
        upsellButton.classList.remove(this.settings.classes.success);
      }, 2000);
    }

    this.headerWrapper.setAttribute(this.settings.attributes.transparent, this.headerIsTransparent);
    this.html.classList.remove(this.settings.classes.htmlClasses);
    this.cartDropdown.classList.remove(this.settings.classes.open);

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  /**
   * Toggle cart dropdown
   *
   * @return  {Void}
   */

  toggleCartDropdown() {
    this.cartDropdownIsOpen = !this.cartDropdownIsOpen;

    if (this.cartDropdownIsOpen) {
      this.openCartDropdown();
    } else {
      this.closeCartDropdown();
    }
  }

  /**
   * Event click to element to open cart dropdown
   *
   * @return  {Void}
   */

  eventToggleCart() {
    this.document.addEventListener('click', (event) => {
      const clickedElement = event.target;
      const isNotCartButton = !(clickedElement.matches(this.settings.elements.cartToggleElement) || clickedElement.closest(this.settings.elements.cartToggleElement));
      const isNotCartDropdownOrCartDropdownChild = !(clickedElement.matches(this.settings.elements.cartDropdown) || clickedElement.closest(this.settings.elements.cartDropdown));
      const isBurgerButton = clickedElement.matches(this.settings.elements.burgerButton) || clickedElement.closest(this.settings.elements.burgerButton);

      if (clickedElement.matches(this.settings.elements.cartToggleElement) || clickedElement.closest(this.settings.elements.cartToggleElement)) {
        event.preventDefault();

        this.toggleCartDropdown();
      } else if (this.cartDropdownIsOpen && isNotCartButton && isNotCartDropdownOrCartDropdownChild) {
        this.cartDropdownIsOpen = false;

        if (isBurgerButton && !this.headerWrapper.classList.contains(this.settings.classes.headerStuck)) {
          this.headerIsTransparent = false;
        }

        this.closeCartDropdown();
      }
    });
  }

  /**
   * Toggle classes on different containers and messages
   *
   * @return  {Void}
   */

  toggleClassesOnContainers() {
    const that = this;

    this.emptyMessage.classList.toggle(that.settings.classes.hidden, that.hasItemsInCart());
    this.buttonHolder.classList.toggle(that.settings.classes.hidden, !that.hasItemsInCart());
    this.itemsHolder.classList.toggle(that.settings.classes.hidden, !that.hasItemsInCart());
  }

  /**
   * Build cart depends on results
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  build(data) {
    if (this.totalItems !== this.newTotalItems) {
      this.totalItems = this.newTotalItems;

      this.toggleClassesOnContainers();
    }

    this.itemsHolder.innerHTML = data;

    this.cartEvents();
    this.initQuantity();
  }

  /**
   * Update cart count
   *
   * @param   {Number}  countItems
   *
   * @return  {Void}
   */

  updateCounter(countItems) {
    if (!this.counterHolders.length) {
      return;
    }

    this.counterHolders.forEach((holder) => {
      holder.innerHTML = countItems;
      holder.setAttribute(settings.elements.cartCountValue, countItems);
    });
  }

  /**
   * Check for items in the cart
   *
   * @return  {Void}
   */

  hasItemsInCart() {
    return this.totalItems > 0;
  }

  /**
   * Build total cart total price
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  buildTotalPrice(data) {
    if (data.original_total_price > data.total_price && data.cart_level_discount_applications.length > 0) {
      this.cartOriginalTotal.classList.remove(this.settings.classes.hidden);
      this.cartOriginaTotalPrice.innerHTML = formatMoney(data.original_total_price, this.settings.formatMoney);
    } else {
      this.cartOriginalTotal.classList.add(this.settings.classes.hidden);
    }

    this.cartTotal.innerHTML = formatMoney(data.total_price, this.settings.formatMoney);

    if (data.cart_level_discount_applications.length > 0) {
      const discountsMarkup = this.buildCartTotalDiscounts(data.cart_level_discount_applications);

      this.cartDiscountHolder.classList.remove(this.settings.classes.hidden);
      this.cartDiscountHolder.innerHTML = discountsMarkup;
    } else {
      this.cartDiscountHolder.classList.add(this.settings.classes.hidden);
    }
  }

  /**
   * Build cart total discounts
   *
   * @param   {Array}  discounts
   *
   * @return  {String}
   */

  buildCartTotalDiscounts(discounts) {
    let discountMarkup = '';

    discounts.forEach((discount) => {
      discountMarkup += Sqrl.render(this.cartTotalDiscountTemplate, {
        discountTitle: discount.title,
        discountTotalAllocatedAmount: formatMoney(discount.total_allocated_amount, this.settings.formatMoney),
      });
    });

    return discountMarkup;
  }

  /**
   * Show/hide free shipping message
   *
   * @param   {Number}  total
   *
   * @return  {Void}
   */

  freeShippingMessageHandle(total) {
    if (this.cartMessage) {
      let cartMessageClass = this.settings.classes.hidden;
      if (this.cartMessage.hasAttribute(this.settings.elements.cartMessageValue) && this.cartMessage.getAttribute(this.settings.elements.cartMessageValue) === 'true' && total !== 0) {
        cartMessageClass = this.settings.classes.success;
      }
      this.cartMessage.classList.toggle(cartMessageClass, total >= this.cartFreeLimitShipping || total === 0);
    }
  }

  /**
   * Set circle progress
   *
   * @param   {Number}  percent
   *
   * @return  {Void}
   */

  setProgress(percent) {
    const offset = this.circumference - ((percent / 100) * this.circumference) / 2;

    this.progress.style.strokeDashoffset = offset;
  }

  /**
   * Update progress when update cart
   *
   * @return  {Void}  [return description]
   */

  updateProgress() {
    const newPercentValue = (this.subtotal / this.cartFreeLimitShipping) * 100;

    this.setProgress(newPercentValue > 100 ? 100 : newPercentValue);
  }
}

window.cart = new CartDrawer();
